import { getPreferences } from './preferences';

// One formatter per time zone setting (Settings → Show times in).
const formatters = {
  local: {
    dateTime: new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }),
    date: new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }),
    time: new Intl.DateTimeFormat(undefined, { timeStyle: 'medium' }),
  },
  utc: {
    dateTime: new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'UTC',
    }),
    date: new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' }),
    time: new Intl.DateTimeFormat(undefined, { timeStyle: 'medium', timeZone: 'UTC' }),
  },
};

export const formatDateTime = (iso: string): string => {
  const zone = getPreferences().timeZone;
  return formatters[zone].dateTime.format(new Date(iso)) + (zone === 'utc' ? ' UTC' : '');
};
/** Clock time with seconds (12:37:39), for timelines. */
export const formatTime = (at: string | number): string =>
  formatters[getPreferences().timeZone].time.format(new Date(at));
export const formatDate = (iso: string): string =>
  formatters[getPreferences().timeZone].date.format(new Date(iso));

export function initials(name: string, email: string): string {
  const source = name.trim() || email;
  const parts = source.split(/[\s@._-]+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '?') + (parts[1]?.[0] ?? '')).toUpperCase();
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

/** Only allow same-app relative redirects (prevents open redirects via ?redirect=). */
export function safeRedirect(target: string | undefined): string {
  return target && target.startsWith('/') && !target.startsWith('//') ? target : '/';
}

export const ROLE_LABELS = { owner: 'Owner', admin: 'Admin', viewer: 'Viewer' } as const;

export const PROVIDER_STATUS_LABELS = {
  untested: 'Untested',
  ok: 'Connected',
  error: 'Error',
} as const;

export const AGENT_STATUS_LABELS = {
  draft: 'Draft',
  active: 'Active',
  archived: 'Archived',
} as const;

export const RUN_STATUS_LABELS = {
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
  cancelled: 'Cancelled',
  timeout: 'Timed out',
} as const;

const integer = new Intl.NumberFormat();
export const formatNumber = (n: number): string => integer.format(n);
const compact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });
/** 57500 → 57.5K. */
export const formatCompact = (n: number): string => compact.format(n);

/** 850 ms, 1.2 s, 2 min 5 s. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.floor(ms / 60_000)} min ${Math.round((ms % 60_000) / 1000)} s`;
}

/** 0.873 → 87%. */
export const formatPercent = (ratio: number | null | undefined): string =>
  ratio === null || ratio === undefined ? '—' : `${Math.round(ratio * 100)}%`;

/** Share of input tokens served from the prompt cache: 800/1000 → 80%; "—" when there is no input. */
export const formatHitRate = (cacheRead: number, input: number): string =>
  input > 0 ? formatPercent(Math.min(1, cacheRead / input)) : '—';

const relative = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
export function formatRelative(iso: string): string {
  const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 60) return relative.format(seconds, 'second');
  if (abs < 3600) return relative.format(Math.round(seconds / 60), 'minute');
  if (abs < 86_400) return relative.format(Math.round(seconds / 3600), 'hour');
  return relative.format(Math.round(seconds / 86_400), 'day');
}

export const RUN_SOURCE_LABELS = {
  gateway: 'Endpoint',
  playground: 'Playground',
  workflow: 'Workflow',
} as const;

/** $12.35, $0.042, $0.000041 (two significant digits below a dollar); `partial` prefixes "≥ ". */
export function formatUsd(
  value: number | null | undefined,
  opts: { partial?: boolean } = {},
): string {
  if (value === null || value === undefined) return '—';
  let text: string;
  if (value === 0 || value >= 1) {
    text = `$${value.toFixed(2)}`;
  } else {
    const decimals = Math.min(8, Math.max(2, Math.ceil(-Math.log10(value)) + 1));
    text = `$${value.toFixed(decimals)}`;
  }
  return opts.partial ? `≥ ${text}` : text;
}

/** Tooltip for every displayed cost (Local Claude runs are subscription-based). */
export const COST_ESTIMATE_NOTE = 'Estimated from models.dev list prices';

/** Aggregate cost label: partial when some runs are unpriced, "—" when none are. */
export function costCell(
  costUsd: number,
  unpricedRuns: number,
): { text: string; title: string | undefined } {
  if (unpricedRuns === 0) return { text: formatUsd(costUsd), title: undefined };
  const who = unpricedRuns === 1 ? '1 run has' : `${unpricedRuns} runs have`;
  const title = `${who} no price (model not in models.dev or before cost tracking). ${COST_ESTIMATE_NOTE}`;
  return costUsd === 0
    ? { text: '—', title }
    : { text: formatUsd(costUsd, { partial: true }), title };
}
